import { FC, useCallback, useEffect, useMemo } from 'react';

import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import { giftItemListForStoreDataAtomFamily } from '@shared/api/giftItem/giftItem.atom';
import { useGiftItemListForStoreHook } from '@shared/api/giftItem/giftItem.hook';
import { BucketImg } from '@shared/components/BucketImg';
import { formatPrice } from '@shared/utils/formatPrice';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { giftStoreNavBarTitleAtom } from '../containers/GiftStoreNavBar/giftStoreNavBar.atom';
import { giftCartAtom, GiftCartItem } from '../state/giftCart.atom';
import {
  calcOrderSurcharge,
  getUnitSurcharge,
  resolveShippingFeeConfig,
} from '../utils/resolveDeliveryFee';

const GiftCartPage: FC = function GiftCartPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bugoId = searchParams.get('bugoId');

  const setTitle = useSetRecoilState(giftStoreNavBarTitleAtom);
  const [cartItems, setCartItems] = useRecoilState(giftCartAtom);

  // 상품 상세 정보 조회 (소량주문 추가금 계산용 — deliveryDetail/브랜드)
  useGiftItemListForStoreHook(bugoId ?? '');
  const giftItemListData = useRecoilValue(
    giftItemListForStoreDataAtomFamily(bugoId ?? ''),
  );

  useEffect(() => {
    setTitle('장바구니');
  }, [setTitle]);

  // 현재 bugoId에 해당하는 장바구니 아이템만 필터링
  const filteredCartItems = useMemo(() => {
    if (!bugoId) return cartItems;
    return cartItems.filter((item) => item.bugoId === bugoId);
  }, [cartItems, bugoId]);

  // 단가 = priceRetail + Σ 선택옵션 추가금
  const cartUnitPrice = useCallback(
    (item: GiftCartItem) =>
      item.priceRetail +
      (item.selectedOptions ?? []).reduce((s, o) => s + (o.priceDelta || 0), 0),
    [],
  );

  // 라인별 소량주문 추가금 — 개당 가산액(면제 기준 이상이면 0)과 면제 기준 수량
  const lineSurcharge = useCallback(
    (item: GiftCartItem) => {
      const info = giftItemListData?.find((gi) => gi._id === item.itemId)?.giftItemInfo;
      return {
        unitSurcharge: getUnitSurcharge(info, item.quantity),
        minQty: resolveShippingFeeConfig(info).freeShippingMinQuantity,
      };
    },
    [giftItemListData],
  );

  // 수량 증가
  const increaseQuantity = useCallback(
    (itemId: string, orderUnit: number) => {
      setCartItems((prev) =>
        prev.map((item) =>
          item.itemId === itemId
            ? { ...item, quantity: item.quantity + orderUnit }
            : item,
        ),
      );
    },
    [setCartItems],
  );

  // 수량 감소
  const decreaseQuantity = useCallback(
    (itemId: string, minOrderQuantity: number, orderUnit: number) => {
      setCartItems((prev) =>
        prev.map((item) => {
          if (item.itemId === itemId) {
            const newQty = item.quantity - orderUnit;
            return newQty >= minOrderQuantity ? { ...item, quantity: newQty } : item;
          }
          return item;
        }),
      );
    },
    [setCartItems],
  );

  // 상품 삭제
  const removeItem = useCallback(
    (itemId: string) => {
      if (confirm('장바구니에서 삭제하시겠습니까?')) {
        setCartItems((prev) => prev.filter((item) => item.itemId !== itemId));
      }
    },
    [setCartItems],
  );

  // 전체 삭제
  const clearCart = useCallback(() => {
    if (confirm('장바구니를 비우시겠습니까?')) {
      if (bugoId) {
        setCartItems((prev) => prev.filter((item) => item.bugoId !== bugoId));
      } else {
        setCartItems([]);
      }
    }
  }, [setCartItems, bugoId]);

  // 총 금액 계산 = 상품 금액(옵션 포함) + 소량주문 추가금
  const totalPrice = useMemo(() => {
    const goods = filteredCartItems.reduce(
      (sum, item) => sum + cartUnitPrice(item) * item.quantity,
      0,
    );
    const surcharge = calcOrderSurcharge(
      filteredCartItems.map((item) => ({
        giftItemInfo: giftItemListData?.find((gi) => gi._id === item.itemId)
          ?.giftItemInfo,
        quantity: item.quantity,
      })),
    );
    return goods + surcharge.total;
  }, [filteredCartItems, cartUnitPrice, giftItemListData]);

  // 총 상품 수
  const totalQuantity = useMemo(() => {
    return filteredCartItems.reduce((sum, item) => sum + item.quantity, 0);
  }, [filteredCartItems]);

  // 결제하기
  const handleCheckout = useCallback(() => {
    if (filteredCartItems.length === 0) {
      alert('장바구니가 비어있습니다.');
      return;
    }
    navigate(`/gift-store/checkout?bugoId=${bugoId}`);
  }, [filteredCartItems, navigate, bugoId]);

  // 상품 상세로 이동
  const goToDetail = useCallback(
    (itemId: string) => {
      navigate(`/gift-store/item/${itemId}?bugoId=${bugoId}`);
    },
    [navigate, bugoId],
  );

  // 장바구니 아이템 렌더링
  const renderCartItem = (item: GiftCartItem) => {
    const { unitSurcharge, minQty } = lineSurcharge(item);
    const itemTotal = (cartUnitPrice(item) + unitSurcharge) * item.quantity;

    return (
      <div key={item.itemId} className="border-b bg-white p-4">
        <div className="flex gap-3">
          {/* 상품 이미지 */}
          <button
            onClick={() => goToDetail(item.itemId)}
            className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100"
          >
            {item.itemImage ? (
              <BucketImg
                value={item.itemImage}
                size="w200"
                alt={item.itemName}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-gray-400">
                No Image
              </div>
            )}
          </button>

          {/* 상품 정보 */}
          <div className="flex-1">
            <button
              onClick={() => goToDetail(item.itemId)}
              className="line-clamp-2 text-left text-sm font-medium"
            >
              {item.itemName}
            </button>
            <p className="mt-1 text-base font-bold">
              {formatPrice(cartUnitPrice(item))}원
            </p>
            {unitSurcharge > 0 && (
              <div className="mt-1 flex items-center gap-1 text-xs">
                <span className="text-amber-800">
                  소량주문{minQty != null ? ` (${minQty}개 미만)` : ''}
                </span>
                <span className="font-bold text-amber-700">
                  +{formatPrice(unitSurcharge)}원/개
                </span>
              </div>
            )}

            {/* 수량 조절 */}
            <div className="mt-2 flex items-center justify-between">
              <div className="flex items-center gap-1">
                <button
                  onClick={() =>
                    decreaseQuantity(item.itemId, item.minOrderQuantity, item.orderUnit)
                  }
                  disabled={item.quantity <= item.minOrderQuantity}
                  className="flex h-8 w-8 items-center justify-center rounded border bg-gray-50 text-lg font-bold disabled:text-gray-300"
                >
                  −
                </button>
                <span className="min-w-[40px] text-center font-bold">
                  {item.quantity}
                </span>
                <button
                  onClick={() => increaseQuantity(item.itemId, item.orderUnit)}
                  className="flex h-8 w-8 items-center justify-center rounded border bg-gray-50 text-lg font-bold"
                >
                  +
                </button>
                {item.orderUnit > 1 && (
                  <span className="ml-1 text-xs text-gray-400">
                    ({item.orderUnit}개 단위)
                  </span>
                )}
              </div>

              {/* 삭제 버튼 */}
              <button
                onClick={() => removeItem(item.itemId)}
                className="text-sm text-gray-400 hover:text-red-500"
              >
                삭제
              </button>
            </div>
          </div>
        </div>

        {/* 상품별 합계 */}
        <div className="mt-2 text-right">
          <span className="text-sm text-gray-500">상품 금액: </span>
          <span className="font-bold text-red-500">{formatPrice(itemTotal)}원</span>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100 pb-32">
      {/* 헤더 영역 */}
      <div className="bg-white p-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">
            총 <span className="font-bold text-black">{filteredCartItems.length}</span>개
            상품
          </span>
          {filteredCartItems.length > 0 && (
            <button
              onClick={clearCart}
              className="text-sm text-gray-400 hover:text-red-500"
            >
              전체 삭제
            </button>
          )}
        </div>
      </div>

      {/* 장바구니 목록 */}
      {filteredCartItems.length > 0 ? (
        <div className="mt-2">{filteredCartItems.map(renderCartItem)}</div>
      ) : (
        <div className="mt-20 text-center">
          <div className="text-6xl">🛒</div>
          <p className="mt-4 text-gray-500">장바구니가 비어있습니다.</p>
          <button
            onClick={() => navigate(`/gift-store?bugoId=${bugoId}`)}
            className="mt-4 rounded-lg bg-gray-800 px-6 py-2 text-white"
          >
            쇼핑하러 가기
          </button>
        </div>
      )}

      {/* 하단 고정 결제 영역 */}
      {filteredCartItems.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 border-t bg-white p-4 shadow-lg">
          <div className="mx-auto max-w-lg">
            {/* 결제 정보 */}
            <div className="mb-3 space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">상품 수량</span>
                <span>{totalQuantity}개</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">총 결제 금액</span>
                <span className="text-xl font-bold text-red-500">
                  {formatPrice(totalPrice)}원
                </span>
              </div>
            </div>

            {/* 결제 버튼 */}
            <button
              onClick={handleCheckout}
              className="w-full rounded-lg bg-red-500 py-3 text-lg font-bold text-white active:bg-red-600"
            >
              {formatPrice(totalPrice)}원 결제하기
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GiftCartPage;
